import _ from 'lodash';
import { observable, computed, action, reaction } from 'mobx';
import { recalculate } from 'components/DocEditor/lineMath';

// The record being edited by a document editor (work order, estimate,
// invoice): what's saved, what's changed since, and saving.
//
// The stores keep the record at `<store>.<slot>.object` and replace it after
// a save or reload. Each time that happens the model "adopts" the new record:
// recalculates its lines (as the old row components did when they mounted)
// and takes a baseline of the fields people edit, so the page knows when
// there are unsaved changes, without the mount-time writes counting as one.

// The fields of a line people can change (and what a save would send).
const LINE_FIELDS = [
  'id', 'product_service_id', 'supplier_id', 'part_number', 'quantity_display_value', 'quantity',
  'rate_per_unit', 'markup', 'unit', 'description', 'work_date', 'override',
];

const lineSnapshot = (item) => ({
  ..._.pick(item, LINE_FIELDS),
  taxes: (item.tax_line_items || []).map((tax) => tax.tax_id),
});

export default class DocModel {
  @observable baseline = null;
  @observable saving = false;

  // config: { kind, noun, getRecord(), saveRecord(), reloadRecord(), fields: [...attribute names], truckCharge(), prepare(attributes) }
  constructor(config) {
    Object.assign(this, config);
    this.stop = reaction(() => this.record, (record) => this.adopt(record), { fireImmediately: true });
  }

  dispose() {
    if (this.stop) this.stop();
  }

  get record() {
    return this.getRecord();
  }

  get attributes() {
    return this.record && this.record.attributes;
  }

  get truck() {
    return this.truckCharge ? this.truckCharge() : null;
  }

  @action
  adopt(record) {
    if (!record || !record.attributes) {
      this.baseline = null;
      return;
    }
    if (this.prepare) this.prepare(record.attributes);
    recalculate(record.attributes, { truckCharge: this.truck });
    this.baseline = this.snapshot();
  }

  snapshot() {
    const attributes = this.attributes;
    if (!attributes) return null;
    return JSON.stringify({
      fields: _.pick(attributes, this.fields || []),
      labour: (attributes.labour || []).map(lineSnapshot),
      materials: (attributes.materials || []).map(lineSnapshot),
      custom: (attributes.custom_line_items || []).map(lineSnapshot),
    });
  }

  @computed get dirty() {
    return this.baseline !== null && this.snapshot() !== this.baseline;
  }

  // After an edit to a line: totals and default lines follow.
  @action
  recalc() {
    if (this.attributes) recalculate(this.attributes, { truckCharge: this.truck });
  }

  @action
  save() {
    if (this.saving) return Promise.reject(new Error('Already saving'));
    this.saving = true;
    return this.saveRecord().then(
      action((response) => { this.saving = false; return response; }),
      action((error) => { this.saving = false; throw error; }),
    );
  }

  // Actions that replace the record from the server without sending it would
  // drop unsaved edits; save them first.
  withSaved(run) {
    return (this.dirty ? this.save() : Promise.resolve()).then(run);
  }

  discard() {
    return this.reloadRecord();
  }
}

// A server error's message, or `fallback`.
export const reason = (error, fallback) => {
  const data = error && error.response && error.response.data;
  const value = data && (data.errors || data.error || data.message);
  if (!value) return fallback;
  if (Array.isArray(value)) return value.join(' ');
  if (typeof value === 'object') return Object.values(value).flat().join(' ');
  return String(value);
};
